import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { VideosFilterParams, VideoType, VideosListResponse, CreateVideoParams, UpdateVideoParams } from '@/types/video'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import { fetchVideos, fetchVideo, createVideo, updateVideo, deleteVideo, bulkDeleteVideos, sortVideo } from '@/lib/api'

type VideoStateType = {
  data: VideoType | null
  isLoading: boolean
  error: string | null
}

type VideosListStateType = {
  data: VideosListResponse | null
  isLoading: boolean
  error: string | null
}

type VideosStoreState = {
  video: VideoStateType
  videosList: VideosListStateType
  filters: VideosFilterParams
  selectedVideoIds: string[]
  setFilters: (filters: Partial<VideosFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchVideos: (params?: VideosFilterParams) => Promise<VideosListResponse>
  resetVideosList: () => void

  // Sort video
  sortVideo: (id: string, type: SortOrderEnum) => Promise<VideoType>

  // Blog Post operations
  getVideo: (id: string) => Promise<VideoType>
  createVideo: (params: CreateVideoParams) => Promise<VideoType>
  updateVideo: (id: string, params: UpdateVideoParams) => Promise<VideoType>
  deleteVideo: (id: string) => Promise<void>
  bulkDeleteVideos: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectVideo: (id: string) => void
  deselectVideo: (id: string) => void
  toggleVideoSelection: (id: string) => void
  selectAllVideos: (videos: VideoType[]) => void
  deselectAllVideos: () => void
  isVideoSelected: (id: string) => boolean
  resetVideo: () => void
}

const defaultFilters: VideosFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'order',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const initialVideoState: VideoStateType = {
  data: null,
  isLoading: false,
  error: null
}

const initialVideosListState: VideosListStateType = {
  data: null,
  isLoading: false,
  error: null
}

const useVideosStore = create<VideosStoreState>()(
  devtools((set, get) => ({
    video: initialVideoState,
    videosList: initialVideosListState,
    selectedVideoIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof VideosFilterParams]
          } else {
            updatedFilters[key as keyof VideosFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: VideosFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as VideosFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof VideosFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getVideo: async id => {
      set(state => ({
        video: {
          ...state.video,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchVideo(id)

        set(state => ({
          video: {
            ...state.video,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          video: {
            ...state.video,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createVideo: async params => {
      set(state => ({
        video: {
          ...state.video,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createVideo(params)

        set(state => ({
          video: {
            ...state.video,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          video: {
            ...state.video,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateVideo: async (id, params) => {
      set(state => ({
        video: {
          ...state.video,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateVideo(id, params)

        set(state => ({
          video: {
            ...state.video,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          video: {
            ...state.video,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteVideo: async id => {
      set(state => ({
        video: {
          ...state.video,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteVideo(id)

        set(state => ({
          video: {
            ...state.video,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          video: {
            ...state.video,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetVideo: () => {
      set(state => ({
        video: {
          ...state.video,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectVideo: id => {
      set(state => {
        if (state.selectedVideoIds.includes(id)) {
          return state
        }
        return { selectedVideoIds: [...state.selectedVideoIds, id] }
      })
    },

    deselectVideo: id => {
      set(state => ({
        selectedVideoIds: state.selectedVideoIds.filter(videoId => videoId !== id)
      }))
    },

    toggleVideoSelection: id => {
      set(state => {
        if (state.selectedVideoIds.includes(id)) {
          return {
            selectedVideoIds: state.selectedVideoIds.filter(videoId => videoId !== id)
          }
        }
        return { selectedVideoIds: [...state.selectedVideoIds, id] }
      })
    },

    selectAllVideos: videos => {
      set(() => ({
        selectedVideoIds: videos.map(video => video.id)
      }))
    },

    deselectAllVideos: () => {
      set(() => ({ selectedVideoIds: [] }))
    },

    isVideoSelected: id => {
      return get().selectedVideoIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteVideos: async ids => {
      set(state => ({
        video: {
          ...state.video,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteVideos({ ids })

        set(state => ({
          video: {
            ...state.video,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedVideoIds: state.selectedVideoIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          video: {
            ...state.video,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchVideos: async (params?: VideosFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        videosList: {
          ...state.videosList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchVideos(filtersToUse)

        set(state => ({
          videosList: {
            ...state.videosList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          videosList: {
            ...state.videosList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetVideosList: () => {
      set(state => ({
        videosList: {
          ...state.videosList,
          data: null,
          error: null
        }
      }))
    },

    sortVideo: async (id, type) => {
      set(() => ({
        video: {
          ...get().video,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await sortVideo(id, type)

        set(() => ({
          video: {
            ...get().video,
            isLoading: false,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          video: {
            ...initialVideoState,
            error: getErrorMessage(error)
          }
        }))
        throw error
      }
    }
  }))
)

export default useVideosStore
